/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   AccountArchivedPage.tsx
 *     Description     :   Rendered INSTEAD of the app when the backend refuses a person as archived: what
 *                         happened and what they can do. It stays until they sign out, because a sign-out
 *                         redirect would take the message with it (owner, 2026-10-31). The wording is the
 *                         shared ERROR_MESSAGES entry, the one the backend sends. Kit + tokens + lucide-react
 *                         only (Constitution XLV).
 *     Author          :   Peter Dobler
 * =========================================================================================
 */
import React from 'react';
import { Archive, LogOut } from 'lucide-react';
import { Card, Heading, Text, Button } from '@/components/ui';
import { ErrorCodes, ERROR_MESSAGES } from '@shared/types/errors';

export interface AccountArchivedPageProps {
  onSignOut: () => void;
}

export const AccountArchivedPage: React.FC<AccountArchivedPageProps> = ({ onSignOut }) => (
  <main className="flex min-h-screen items-center justify-center bg-[var(--color-bg-page)] px-4">
    <Card padding="lg" className="w-full max-w-lg text-center">
      <Archive aria-hidden className="mx-auto mb-4 h-10 w-10 text-[var(--color-status-warning)]" />
      <Heading as="h1" level="h4">Account archived</Heading>
      <Text className="mt-3">{ERROR_MESSAGES[ErrorCodes.ACCOUNT_ARCHIVED]}</Text>
      <Text color="muted" className="mt-2">
        Contact your administrator if you think this is a mistake, or sign out to use a different account.
      </Text>
      <div className="mt-6 flex justify-center">
        <Button variant="secondary" leftIcon={<LogOut className="h-4 w-4" />} onClick={onSignOut}>
          Sign out
        </Button>
      </div>
    </Card>
  </main>
);

export default AccountArchivedPage;
